import type { MaintenanceStatusResponse, BannerConfig } from '../models/maintenance';

const DEFAULT_CONFIG: BannerConfig = {
  show: false,
  type: 'information',
  title: '',
  dismissible: false,
  showCountdown: false,
};

function mapSeverityToBannerType(severity?: 'info' | 'warning' | 'critical'): 'information' | 'warning' | 'error' {
  switch (severity) {
    case 'warning':
      return 'warning';
    case 'critical':
      return 'error';
    case 'info':
    default:
      return 'information';
  }
}

function formatScheduledTime(date: Date): string {
  const options: Intl.DateTimeFormatOptions = {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  };
  return date.toLocaleString(navigator.language, options);
}

function createActiveMaintenanceBanner(status: MaintenanceStatusResponse): BannerConfig {
  const title = status.userHasAdminAccess
    ? 'Maintenance Mode Active - Admin Access Only'
    : 'System Under Maintenance';

  return {
    show: true,
    type: mapSeverityToBannerType(status.severity),
    title,
    message: status.message ?? 'The system is currently undergoing maintenance. Please try again later.',
    dismissible: false,
    showCountdown: false,
  };
}

function createImminentBanner(status: MaintenanceStatusResponse, scheduledStart: Date): BannerConfig {
  return {
    show: true,
    type: 'error',
    title: 'Maintenance Starting Soon - Save Your Work Now!',
    message: status.message ?? 'System maintenance will begin shortly. Please save all your work immediately.',
    dismissible: false,
    showCountdown: true,
    countdownTarget: scheduledStart,
  };
}

function createUpcomingBanner(status: MaintenanceStatusResponse, scheduledStart: Date): BannerConfig {
  return {
    show: true,
    type: 'warning',
    title: 'Upcoming Maintenance - Save Your Work',
    message: status.message ?? 'System maintenance is scheduled soon. Please complete and save your current work.',
    dismissible: false,
    showCountdown: true,
    countdownTarget: scheduledStart,
  };
}

function createScheduledBanner(status: MaintenanceStatusResponse, scheduledStart: Date): BannerConfig {
  const formattedDate = formatScheduledTime(scheduledStart);
  return {
    show: true,
    type: 'information',
    title: 'Scheduled Maintenance',
    message: status.message
      ? `${status.message} (Scheduled for ${formattedDate})`
      : `System maintenance is scheduled for ${formattedDate}. Please plan accordingly.`,
    dismissible: true,
    showCountdown: false,
  };
}

function getScheduledBanner(status: MaintenanceStatusResponse): BannerConfig {
  if (!status.scheduledStartTime) return DEFAULT_CONFIG;
  const scheduledStart = new Date(status.scheduledStartTime);
  const hoursUntilMaintenance = (scheduledStart.getTime() - Date.now()) / (1000 * 60 * 60);

  if (hoursUntilMaintenance <= 0) return DEFAULT_CONFIG;
  if (hoursUntilMaintenance <= 0.5) return createImminentBanner(status, scheduledStart);
  if (hoursUntilMaintenance <= 2) return createUpcomingBanner(status, scheduledStart);
  return createScheduledBanner(status, scheduledStart);
}

export function calculateBannerConfig(status: MaintenanceStatusResponse | null): BannerConfig {
  if (!status) return DEFAULT_CONFIG;
  if (status.isMaintenanceMode) return createActiveMaintenanceBanner(status);
  if (status.scheduledStartTime) return getScheduledBanner(status);
  return DEFAULT_CONFIG;
}

export function calculatePollingInterval(status: MaintenanceStatusResponse | null): number {
  if (!status?.scheduledStartTime) {
    return 300_000;
  }

  const now = new Date();
  const scheduledStart = new Date(status.scheduledStartTime);
  const hoursUntilMaintenance = (scheduledStart.getTime() - now.getTime()) / (1000 * 60 * 60);

  if (hoursUntilMaintenance <= 0) {
    return 300_000;
  }
  if (hoursUntilMaintenance <= 0.5) {
    return 30_000;
  }
  if (hoursUntilMaintenance <= 2) {
    return 60_000;
  }
  return 300_000;
}
